import React, { useState } from "react";
import { ShieldCheck, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { signPdfWithAutoFirma } from "@/lib/autofirma";
import {
    getFilledAnexoSignPayload,
    uploadSignedAnexo,
    api,
} from "@/lib/api";

/**
 * SignAnnexButton — triggers AutoFirma to sign a filled annex (PAdES) and
 * uploads the signed PDF back to the server.
 *
 * Props:
 *   annexId – id of the FilledAnnex record
 *   isSigned – whether the annex already has a signed version
 *   signerDn – the DN string already stored (when isSigned)
 *   onSigned(updatedAnnex) – called after a successful signature
 */
export function SignAnnexButton({ annexId, isSigned, signerDn, onSigned, compact }) {
    const [signing, setSigning] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    const onClick = async () => {
        setSigning(true);
        try {
            const payload = await getFilledAnexoSignPayload(annexId);
            const result = await signPdfWithAutoFirma(payload.pdf_base64);
            const persisted = await uploadSignedAnexo(
                annexId,
                result.signedB64,
                result.dn
            );
            toast.success("Anexo firmado con AutoFirma");
            if (onSigned) onSigned(persisted);
        } catch (err) {
            const msg = err?.message || "No se pudo firmar el anexo";
            if (
                /no está instalado|AutoScript no se cargó|no se pudo descargar/i.test(
                    msg
                )
            ) {
                setHelpOpen(true);
            } else if (/cancelada por el usuario/i.test(msg)) {
                toast.info("Firma cancelada");
            } else {
                toast.error(msg);
            }
        } finally {
            setSigning(false);
        }
    };

    const onDownloadSigned = async () => {
        try {
            const res = await api.get(
                `/anexos/cumplimentados/${annexId}/download/signed-pdf`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "anexo-firmado.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("No se pudo descargar el PDF firmado");
        }
    };

    if (isSigned) {
        return (
            <>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onDownloadSigned}
                    className={`rounded-full ${compact ? "h-9 px-3" : "h-10 px-4"} border-emerald-300 bg-emerald-50 text-emerald-700 font-bold text-xs gap-1.5 hover:bg-emerald-100`}
                    title={signerDn ? `Firmado por: ${signerDn}` : "Anexo firmado"}
                    data-testid={`anexo-signed-${annexId}`}
                >
                    <ShieldCheck size={13} /> Firmado · descargar
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={onClick}
                    disabled={signing}
                    className="rounded-full h-9 w-9 p-0 text-ink-400 hover:text-brand-600"
                    title="Volver a firmar"
                    data-testid={`anexo-resign-${annexId}`}
                >
                    {signing ? (
                        <Loader2 size={13} className="animate-spin" />
                    ) : (
                        <ShieldCheck size={13} />
                    )}
                </Button>
            </>
        );
    }

    return (
        <>
            <Button
                type="button"
                size="sm"
                onClick={onClick}
                disabled={signing}
                className={`rounded-full ${compact ? "h-9 px-3" : "h-10 px-4"} bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs gap-1.5 shadow-pop disabled:opacity-60`}
                title="Firmar con AutoFirma (certificado digital local)"
                data-testid={`anexo-sign-${annexId}`}
            >
                {signing ? (
                    <>
                        <Loader2 size={13} className="animate-spin" />
                        Firmando…
                    </>
                ) : (
                    <>
                        <ShieldCheck size={13} />
                        Firmar con AutoFirma
                    </>
                )}
            </Button>

            <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle size={18} className="text-amber-600" />
                            AutoFirma no detectada
                        </DialogTitle>
                        <DialogDescription className="pt-2 space-y-3 text-sm">
                            <p>
                                <strong>AutoFirma</strong> es la aplicación oficial gratuita
                                del Gobierno de España para firmar documentos PDF con tu
                                certificado digital (FNMT, DNIe, Cl@ve PIN, etc.). Sin ella
                                no podemos firmar localmente en tu navegador.
                            </p>
                            <p className="font-bold text-ink-950">¿Cómo se instala?</p>
                            <ol className="list-decimal list-inside space-y-1 text-ink-700">
                                <li>
                                    Descarga la última versión para tu sistema (Windows /
                                    macOS / Linux).
                                </li>
                                <li>Instálala (~2 minutos).</li>
                                <li>
                                    Vuelve aquí y pulsa <strong>Firmar con AutoFirma</strong>{" "}
                                    de nuevo.
                                </li>
                            </ol>
                            <a
                                href="https://firmaelectronica.gob.es/Home/Descargas.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-brand-600 font-bold hover:underline"
                                data-testid="autofirma-install-link"
                            >
                                Descargar AutoFirma
                                <ExternalLink size={12} />
                            </a>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={() => setHelpOpen(false)}
                            className="rounded-full"
                            data-testid="autofirma-help-close"
                        >
                            Entendido
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
